import React, { ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icons } from "../_assets";
import { Icon } from "../_modelsTS/Icon";

type HorizontalFilterDropdownProps = {
    label: string;
    activeCount: number;
    children: ReactNode;
};

export function HorizontalFilterDropdown({
    label,
    activeCount,
    children,
}: HorizontalFilterDropdownProps) {
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isOpen) return;
        const onMouseDown = (e: MouseEvent) => {
            if (!wrapperRef.current?.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", onMouseDown);
        return () => document.removeEventListener("mousedown", onMouseDown);
    }, [isOpen]);

    const isActive = activeCount > 0;

    return (
        <div ref={wrapperRef} className="position-relative">
            <div
                onClick={() => setIsOpen((open) => !open)}
                className={`
                    d-flex align-items-center text-nowrap
                    px-3 py-2 c-rounded-big
                    c-bd-220 cp
                    c-font-weight-medium
                    c-transition-all
                    ${
                        isActive
                            ? "c-bg-primary-light-2 c-text-primary font-weight-bold"
                            : "c-bg-grey-250 c-hover-bg-primary-light-2"
                    }
                `}
            >
                <span>{label}</span>
                {isActive && (
                    <span className="ml-2 font-weight-bold">
                        ({activeCount})
                    </span>
                )}
                <img
                    className="c-img-1rem ml-2"
                    src={Icons.arrowDown}
                    alt="arrow"
                />
            </div>
            <div
                className="position-absolute bg-white c-rounded-big c-box-shadow border mt-1 c-mh-20rem overflow-auto"
                style={{
                    minWidth: 220,
                    zIndex: 1000,
                    display: isOpen ? "block" : "none",
                }}
            >
                {children}
            </div>
        </div>
    );
}

type FilterCheckboxItemProps = {
    checked: boolean;
    label: string;
    onClick: () => void;
    itemsAmount?: number;
    icon?: Icon;
};

type SingleSelectOption = { value: any; label: string };

type HorizontalSingleSelectFilterProps = {
    label: string;
    options: SingleSelectOption[];
    value: any;
    onChange: (value: any) => void;
    isActive?: boolean;
    isSearchable?: boolean;
    // Fired when the dropdown transitions from closed to open. Lets callers
    // lazily load the options the first time the user opens it.
    onOpen?: () => void;
};

export function HorizontalSingleSelectFilter({
    label,
    options,
    value,
    onChange,
    isActive = false,
    isSearchable = false,
    onOpen,
}: HorizontalSingleSelectFilterProps) {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isOpen) return;
        const onMouseDown = (e: MouseEvent) => {
            if (!wrapperRef.current?.contains(e.target as Node)) {
                setIsOpen(false);
                setSearch("");
            }
        };
        document.addEventListener("mousedown", onMouseDown);
        return () => document.removeEventListener("mousedown", onMouseDown);
    }, [isOpen]);

    const selected = options.find((o) => o.value === value);
    const filteredOptions =
        isSearchable && search
            ? options.filter((o) =>
                  o.label.toLowerCase().includes(search.toLowerCase()),
              )
            : options;

    const toggleOpen = () => {
        if (!isOpen) onOpen?.();
        setIsOpen((o) => !o);
    };

    return (
        <div ref={wrapperRef} className="position-relative">
            <div
                onClick={toggleOpen}
                className={`
                    d-flex align-items-center text-nowrap
                    px-3 py-2 c-rounded-big
                    c-bd-220 cp
                    c-font-weight-medium
                    c-transition-all
                    ${
                        isActive
                            ? "c-bg-primary-light-2 c-text-primary font-weight-bold"
                            : "c-bg-grey-250 c-hover-bg-primary-light-2"
                    }
                `}
            >
                <span className="mr-2">{label}:</span>
                <span>{selected?.label ?? "-"}</span>
                <img
                    className="c-img-1rem ml-2"
                    src={Icons.arrowDown}
                    alt="arrow"
                />
            </div>
            <div
                className="position-absolute bg-white c-rounded-big c-box-shadow border mt-1 c-mh-20rem overflow-auto"
                style={{
                    minWidth: 220,
                    zIndex: 1000,
                    display: isOpen ? "block" : "none",
                }}
            >
                {isSearchable && (
                    <div className="px-2 py-1 border-bottom">
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-100 c-no-focus-outline border-0 p-1"
                            placeholder={t("global_search_placeholder")}
                            autoFocus
                        />
                    </div>
                )}
                {filteredOptions.map((opt) => (
                    <div
                        key={String(opt.value)}
                        onClick={() => {
                            onChange(opt.value);
                            setIsOpen(false);
                            setSearch("");
                        }}
                        className={`px-3 py-2 cp c-hover-bg-primary-light-2 text-nowrap ${
                            opt.value === value
                                ? "c-bg-grey-240 font-weight-bold"
                                : ""
                        }`}
                    >
                        {opt.label}
                    </div>
                ))}
            </div>
        </div>
    );
}

type MultiSelectOption = {
    value: any;
    label: string;
    itemsAmount?: number;
    icon?: Icon;
};

type HorizontalMultiSelectFilterProps = {
    label: string;
    options: MultiSelectOption[];
    values: any[];
    onChange: (values: any[]) => void;
    isSearchable?: boolean;
    align?: "left" | "right";
};

export function HorizontalMultiSelectFilter({
    label,
    options,
    values,
    onChange,
    isSearchable = false,
    align = "left",
}: HorizontalMultiSelectFilterProps) {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isOpen) return;
        const onMouseDown = (e: MouseEvent) => {
            if (!wrapperRef.current?.contains(e.target as Node)) {
                setIsOpen(false);
                setSearch("");
            }
        };
        document.addEventListener("mousedown", onMouseDown);
        return () => document.removeEventListener("mousedown", onMouseDown);
    }, [isOpen]);

    const activeCount = values.length;
    const isActive = activeCount > 0;
    const filteredOptions =
        isSearchable && search
            ? options.filter((o) =>
                  o.label.toLowerCase().includes(search.toLowerCase()),
              )
            : options;

    const toggle = (value: any) => {
        if (values.includes(value)) {
            onChange(values.filter((v) => v !== value));
        } else {
            onChange([...values, value]);
        }
    };

    return (
        <div ref={wrapperRef} className="position-relative">
            <div
                onClick={() => setIsOpen((o) => !o)}
                className={`
                    d-flex align-items-center text-nowrap
                    px-3 py-2 c-rounded-big
                    c-bd-220 cp
                    c-font-weight-medium
                    c-transition-all
                    ${
                        isActive
                            ? "c-bg-primary-light-2 c-text-primary font-weight-bold"
                            : "c-bg-grey-250 c-hover-bg-primary-light-2"
                    }
                `}
            >
                <span>{label}</span>
                {isActive && (
                    <span className="ml-2 font-weight-bold">
                        ({activeCount})
                    </span>
                )}
                <img
                    className="c-img-1rem ml-2"
                    src={Icons.arrowDown}
                    alt="arrow"
                />
            </div>
            <div
                className="position-absolute bg-white c-rounded-big c-box-shadow border mt-1 c-mh-20rem overflow-auto"
                style={{
                    minWidth: 220,
                    zIndex: 1000,
                    display: isOpen ? "block" : "none",
                    left: align === "right" ? "auto" : 0,
                    right: align === "right" ? 0 : "auto",
                }}
            >
                {isSearchable && (
                    <div className="px-2 py-1 border-bottom">
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-100 c-no-focus-outline border-0 p-1"
                            placeholder={t("global_search_placeholder")}
                        />
                    </div>
                )}
                {filteredOptions.map((opt) => (
                    <FilterCheckboxItem
                        key={String(opt.value)}
                        checked={values.includes(opt.value)}
                        label={opt.label}
                        itemsAmount={opt.itemsAmount}
                        icon={opt.icon}
                        onClick={() => toggle(opt.value)}
                    />
                ))}
            </div>
        </div>
    );
}

export function FilterCheckboxItem({
    checked,
    label,
    onClick,
    itemsAmount,
    icon,
}: FilterCheckboxItemProps) {
    return (
        <div
            onClick={onClick}
            className="d-flex align-items-center px-3 py-2 cp c-hover-bg-primary-light-2"
        >
            <input
                type="checkbox"
                checked={checked}
                readOnly
                className="mr-2"
            />
            {icon?.src && (
                <img
                    className="c-img-1rem c-min-h-1 c-min-w-1 mr-2"
                    src={icon.src}
                    alt={icon.alt}
                />
            )}
            <span className="flex-grow-1 text-nowrap">{label}</span>
            {itemsAmount !== undefined && !isNaN(itemsAmount) && (
                <span className="ml-3" style={{ opacity: 0.7 }}>
                    {itemsAmount}
                </span>
            )}
        </div>
    );
}
